import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Dialog, InlineBanner, LinkButton, notify, RadioGroup } from '../../../components/ui';
import { useApiError } from '../../../hooks/useApiError';
import api from '../../../services/api';
import { getTranslation } from '../../../utils/translation.utils';
import type { LocationRecord, MenuRecord } from '../apiTypes';

interface Props {
    location: LocationRecord;
    canChange: boolean;
    onChanged: () => void;
}

export const ActiveMenuCard = ({ location, canChange, onChanged }: Props) => {
    const { t, i18n } = useTranslation('locations');
    const apiError = useApiError();
    const [open, setOpen] = useState(false);
    const [menus, setMenus] = useState<MenuRecord[] | null>(null);
    const [choice, setChoice] = useState('');
    const [saving, setSaving] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const name = (m: MenuRecord) => getTranslation(m.translations, 'name', m.name, i18n.language);
    const menu = location.activeMenu;

    const openDialog = () => {
        setChoice(location.activeMenuId ?? '');
        setLoadFailed(false);
        setOpen(true);
        api.get<MenuRecord[]>('/menus')
            .then((r) => setMenus(r.data.filter((m) => m.restaurantId === undefined || m.restaurantId === location.restaurantId)))
            .catch(() => setLoadFailed(true));
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/locations/${location.id}`, { activeMenuId: choice || null });
            notify.success(t('menu.changed'));
            setOpen(false);
            onChanged();
        } catch (e) {
            notify.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section aria-labelledby="active-menu-heading">
            <Card padding="lg">
            <h2 id="active-menu-heading" className="text-title text-strong">
                {t('menu.title')}
            </h2>
            {menu ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="text-heading text-strong">{name(menu)}</p>
                        <p className="text-small text-muted">{t('menu.categories', { count: menu.categories?.length ?? 0 })}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {canChange && (
                            <Button variant="outline" onClick={openDialog}>
                                {t('menu.change')}
                            </Button>
                        )}
                        <LinkButton to={`/menus/edit/${menu.id}`} variant="outline">
                            {t('menu.edit')}
                        </LinkButton>
                    </div>
                </div>
            ) : (
                <InlineBanner
                    tone="warning"
                    className="mt-3"
                    action={canChange ? { label: t('menu.choose'), onClick: openDialog } : undefined}
                >
                    {t('menu.none')}
                </InlineBanner>
            )}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                busy={saving}
                title={t('menu.dialogTitle')}
                description={t('menu.dialogBody')}
                footer={
                    <>
                        <Button variant="quiet" onClick={() => setOpen(false)} disabled={saving}>
                            {t('common:ui.cancel')}
                        </Button>
                        <Button variant="primary" onClick={save} loading={saving} disabled={!menus}>
                            {t('common:ui.save')}
                        </Button>
                    </>
                }
            >
                {loadFailed ? (
                    <p role="alert" className="text-small text-danger">
                        {t('menu.loadFailed')}
                    </p>
                ) : menus ? (
                    <RadioGroup
                        aria-label={t('menu.title')}
                        value={choice}
                        onChange={setChoice}
                        options={[{ value: '', label: t('form.noMenu') }, ...menus.map((m) => ({ value: m.id, label: name(m) }))]}
                    />
                ) : (
                    <p className="text-small text-muted">{t('common:ui.loading')}</p>
                )}
            </Dialog>
            </Card>
        </section>
    );
};
